import { api } from "@/lib/api";
import { ApnAddressSearchResult } from "@/types";
import { ActionIcon, Button, Combobox, InputBase, ScrollArea, Table, Tabs, Title, useCombobox } from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconClipboardSearch, IconHomeSearch, IconSearch } from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import { CountyMask } from "../CountySelect";


export interface GuardPropertyModuleProps {
    selectedCounty: CountyMask | null;
    onGuardProperty: (guardRecordSubscription: { county: string, apn: string, description?: string }) => void;
    guardedCountyProperties?: { county: string, apn: string, description?: string }[];
}

const GuardPropertyModule: React.FC<GuardPropertyModuleProps> = ({ selectedCounty, onGuardProperty, guardedCountyProperties }) => {
    const [guardedProperties, setGuardedProperties] = useState<{ county: string, apn: string, description?: string }[]>(guardedCountyProperties ?? []);
    const [rawAddressSearch, setRawAddressSearch] = useState<string>('');
    const [addressSearch, setAddressSearch] = useDebouncedState<string>('', 300);
    const [addressSearchResult, setAddressSearchResult] = useState<ApnAddressSearchResult[]>([]);
    const [rawApnSearch, setRawApnSearch] = useState<string>('');
    const [apnSearch, setApnSearch] = useDebouncedState<string>('', 300);
    const [apnSearchResults, setApnSearchResults] = useState<ApnAddressSearchResult[]>([]);
    const onAddressSearchClick = async () => {
        if (addressSearch.trim().length > 0) {
            try {
                const searchResult: ApnAddressSearchResult[] = await api.get(`/search/street?SearchTerm=${addressSearch}&County=${selectedCounty?.name}`);
                setAddressSearchResult(searchResult);
                if (searchResult.length > 0) {
                }
            } catch (error) {
                console.error(error);
                notifications.show({ title: 'Error', message: 'Failed to search APNs', color: 'red', position: 'top-center', "aria-live": "polite", closeButtonProps: { "aria-label": "Close error notification" } });
            }
        }
        else {
            setAddressSearchResult([]);
        }
    }
    const apnExists = (apn: string): boolean => {
        return guardedProperties.some((guardApnSubscription) => guardApnSubscription.apn === apn.trim() && guardApnSubscription.county === selectedCounty?.name);
    }
    //Need to allow for spaces in raw input and leave them in the formatted version if the mask includes them
    //Mask definitions: 0 = number, a = letter, - = dash, c=number, letter, or space all other characters are treated as literals and included in the masked value
    const maskValue = (value: string, mask: string): string => {
        const raw = mask.includes('s') ? value.replace(/[^a-zA-Z0-9\s]/g, '') : value.replace(/[^a-zA-Z0-9]/g, '');
        let maskedValue = '';
        let rawIndex = 0;
        for (let i = 0; i < mask.length && rawIndex < raw.length; i++) {
            const patternChar = mask[i];
            const currentRawChar = raw[rawIndex];
            if (patternChar === '0' && /\d/.test(currentRawChar)) {
                maskedValue += currentRawChar;
                rawIndex++;
            }
            else if (patternChar === 'a' && /[a-zA-Z]/.test(currentRawChar)) {
                maskedValue += currentRawChar.toUpperCase();
                rawIndex++;
            } else if (patternChar === '-') { // dash
                maskedValue += '-';
            } else if (patternChar === 'c') { // any character
                maskedValue += currentRawChar.toUpperCase();
                rawIndex++;
            } else {
                maskedValue += patternChar;
            }
        }
        return maskedValue;
    }
    const onAddressSearchChanges = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setRawAddressSearch(value);
        setAddressSearch(value);
    }, [setAddressSearch]);

    useEffect(() => {},[guardedCountyProperties])
    useEffect(() => {
        const searchAddress = async () => {
            if (addressSearch.trim().length > 0) {
                try {
                    const searchResult: ApnAddressSearchResult[] = await api.get(`/search/address?SearchTerm=${addressSearch}&County=${selectedCounty?.name}`);
                    setAddressSearchResult(searchResult);
                    if (searchResult.length > 0) {
                    }
                } catch (error) {
                    console.error(error);
                    notifications.show({ title: 'Error', message: 'Failed to search APNs', color: 'red', position: 'top-center', "aria-live": "polite", closeButtonProps: { "aria-label": "Close error notification" } });
                }
            }
            else {
                setAddressSearchResult([]);
            }
        }
        searchAddress();
    }, [addressSearch])
    const onApnSearchChanges = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const maskedValue = maskValue(event.currentTarget.value, selectedCounty?.mask ?? '');
        setRawApnSearch(maskedValue);
        setApnSearch(maskedValue);
    }, [setApnSearch, selectedCounty])
    useEffect(() => {
        const searchApns = async () => {
            if (apnSearch.trim().length > 0) {
                try {
                    const searchResult: ApnAddressSearchResult[] = await api.get(`/search/apn?SearchTerm=${apnSearch}&County=${selectedCounty?.name}`);
                    setApnSearchResults(searchResult);
                    if (searchResult.length > 0) {
                    }
                } catch (error) {
                    console.error(error);
                    notifications.show({ title: 'Error', message: 'Failed to search APNs', color: 'red', position: 'top-center', "aria-live": "polite", closeButtonProps: { "aria-label": "Close error notification" } });
                }
            }
            else {
                setApnSearchResults([]);
            }
        }
        searchApns();
    }, [apnSearch]);

    const apnSearchCombobox = useCombobox({
        onDropdownClose: () => {
            apnSearchCombobox.resetSelectedOption();
        }
    });
    const addressSearchCombobox = useCombobox({
        onDropdownClose: () => {
            addressSearchCombobox.resetSelectedOption();
        }
    });
    useEffect(() => {
        if (selectedCounty) {
            setAddressSearchResult([]);
            setAddressSearch('');
            setRawAddressSearch('');
            addressSearchCombobox.resetSelectedOption();
            setApnSearchResults([]);
            setApnSearch('');
            setRawApnSearch('');
            apnSearchCombobox.resetSelectedOption();
            setGuardedProperties(guardedProperties ?? []);
        }
    }, [selectedCounty]);

    useEffect(() => {
        setGuardedProperties(guardedCountyProperties ?? []);
        setAddressSearchResult([...addressSearchResult.map((item) => { item.loading = false; return item; })]);
        setApnSearchResults([...apnSearchResults.map((item) => { item.loading = false; return item; })]);
    }, [guardedCountyProperties])

    return (
        <>
            <Title my={10} order={6}>Step 2: Find your property by Address or APN</Title>
            <Tabs defaultValue='addressSearch' variant="outline">
                <Tabs.List mb='md'>
                    <Tabs.Tab value="addressSearch" leftSection={<IconHomeSearch size={20} />}>Address Search</Tabs.Tab>
                    <Tabs.Tab value="apnSearch" leftSection={<IconClipboardSearch size={20} />}>APN Search</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="addressSearch">
                    <Combobox
                        store={addressSearchCombobox}
                        withinPortal={false}
                        onOptionSubmit={(value) => {
                            setAddressSearch(value);
                            setAddressSearchResult(addressSearchResult.filter((item) => item.address === value));
                        }}
                    >
                        <Combobox.Target>
                            <InputBase
                                label="House Number and Street Name"
                                placeholder="House Number and Street Name"
                                description="Enter a house number and street name only. (Example: 1516 BOULDER)"
                                title="House Number and Street Name"
                                data-1p-ignore
                                data-bwignore
                                data-lpignore="true"
                                data-form-type="other"
                                value={rawAddressSearch}
                                type="text"
                                styles={{ section: { width: '100px', marginRight: '0px' } }}
                                name="houseNumberAndStreetName"
                                rightSection={
                                    <Button w={100} onClick={onAddressSearchClick}>Search</Button>
                                }
                                onChange={onAddressSearchChanges}
                            />
                        </Combobox.Target>
                        <Combobox.Dropdown>
                            <ScrollArea.Autosize type="scroll" mah={200}>
                                {addressSearchResult.slice(0, 10).map((result, i) => (
                                    <Combobox.Option
                                        key={i}
                                        value={result.address}>{result.address}</Combobox.Option>
                                ))}
                            </ScrollArea.Autosize>
                        </Combobox.Dropdown>
                    </Combobox>
                    {addressSearchResult.length > 0 && <ScrollArea.Autosize mt={20} mih={200} mah={250} type="auto">
                        <Table stickyHeader>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>APN</Table.Th>
                                    <Table.Th>Address</Table.Th>
                                    <Table.Th></Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {addressSearchResult.map((result) => (
                                    <Table.Tr key={result.apn}>
                                        <Table.Td>{result.apn}</Table.Td>
                                        <Table.Td>{`${result.address} ${result.cityStateZip}`}</Table.Td>
                                        <Table.Td>
                                            <Button
                                                variant='outline'
                                                miw={120}
                                                color={apnExists(result.apn) ? 'green' : 'pq-red'}
                                                loading={result.loading}
                                                onClick={() => {
                                                    if (apnExists(result.apn)) {
                                                        return;
                                                    }
                                                    addressSearchResult.forEach((item) => {
                                                        if (item.apn == result.apn) {
                                                            item.loading = true
                                                        }
                                                    });
                                                    setAddressSearchResult([...addressSearchResult])
                                                    onGuardProperty({ county: result.county, description: `${result.address} ${result.cityStateZip}`, apn: result.apn });
                                                }}
                                            >{apnExists(result.apn) ? 'Guarded' : 'Guard Property'}</Button>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea.Autosize>}
                </Tabs.Panel>
                <Tabs.Panel value="apnSearch">
                    <Combobox
                        store={apnSearchCombobox}
                        withinPortal={false}
                        onOptionSubmit={(value) => {
                            setApnSearch(value);
                            setApnSearchResults([...apnSearchResults.filter((item) => item.apn === value)]);
                        }}
                    >
                        <Combobox.Target>
                            <InputBase
                                label="APN"
                                placeholder="APN"
                                description={selectedCounty && `${selectedCounty.name} APN Format: ${selectedCounty.format}`}
                                title="APN"
                                value={rawApnSearch}
                                type="text"
                                name="apn"
                                data-1p-ignore
                                data-bwignore
                                data-lpignore="true"
                                data-form-type="other"
                                onChange={onApnSearchChanges}
                                rightSection={<ActionIcon
                                    radius='xl'
                                    size='md'
                                    color="pq-red"
                                    aria-label="Search for APN"
                                    variant="transparent">
                                    <IconSearch size={20} /></ActionIcon>
                                }

                            />
                        </Combobox.Target>
                        <Combobox.Dropdown>
                            <Combobox.Options>
                                <ScrollArea.Autosize type="scroll" mah={200}>
                                    {apnSearchResults.slice(0, 10).map((result, i) => (
                                        <Combobox.Option
                                            key={i}
                                            value={result.apn}>{result.apn}</Combobox.Option>
                                    ))}
                                </ScrollArea.Autosize>
                            </Combobox.Options>
                        </Combobox.Dropdown>
                    </Combobox>
                    {apnSearchResults.length > 0 && <ScrollArea.Autosize mt={20} mih={200} mah={250} type="auto">
                        <Table stickyHeader>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>APN</Table.Th>
                                    <Table.Th>Address</Table.Th>
                                    <Table.Th></Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {apnSearchResults.map((result) => (
                                    <Table.Tr key={result.apn}>
                                        <Table.Td>{result.apn}</Table.Td>
                                        <Table.Td>{`${result.address} ${result.cityStateZip}`}</Table.Td>
                                        <Table.Td>
                                            <Button
                                                variant='outline'
                                                miw={120}
                                                color={apnExists(result.apn) ? 'green' : 'pq-red'}
                                                loading={result.loading}
                                                onClick={() => {
                                                    if (apnExists(result.apn)) {
                                                        return;
                                                    }
                                                    apnSearchResults.forEach((item) => {
                                                        if (item.apn == result.apn) {
                                                            item.loading = true
                                                        }
                                                    });
                                                    setApnSearchResults([...apnSearchResults])
                                                    onGuardProperty({ county: result.county, description: `${result.address} ${result.cityStateZip}`, apn: result.apn });
                                                }}
                                            >{apnExists(result.apn) ? 'Guarded' : 'Guard Property'}</Button>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea.Autosize>}
                </Tabs.Panel>
            </Tabs>
        </>
    )
}

export default GuardPropertyModule;